import { apiClient } from '@/plugins/axios/api'
import type { Project, ProjectSubmission } from '@/models/interfaces/Project'
import { FormType } from '@/models/enums/app/FormType'
import FileUploader from '@/services/files/FileUploader'
import type { BaseMediaObject } from '@/models/interfaces/object/MediaObject'
import {
  createEmptyProjectFilterOptions,
  type ProjectFilterOptions,
} from '@/models/interfaces/map/PlatformFilterOptions'
import type { ProjectMapFilters } from '@/models/interfaces/map/ProjectMapFilters'
import { skipPlatformFilterOptionsEndpoint } from '@/config/env'
import { transformSymfonyRelationToIRIs } from '@/services/utils/UtilsService'

const PROJECT_COLLECTION_MAX_ITEMS_PER_PAGE = 500

/** After a failed filter_options response in this tab, skip that URL for the rest of the session. */
let projectFilterOptionsEndpointSkippedForSession = false

export class ProjectService {
  /**
   * GET /api/projects, optionally scoped to a commune perimeter (GET /api/projects?cmnCode=...):
   * a real spatial test (ST_Intersects), not a match on the project's assigned commune field.
   * An unknown cmnCode 404s; like the atlas_categories/infrastructures equivalent, that's caught
   * and treated as an empty result rather than surfaced as an error.
   */
  static async getAll(cmnCode?: string | null): Promise<Project[]> {
    try {
      const response = await apiClient.get('/api/projects', {
        params: cmnCode ? { cmnCode } : undefined,
      })
      const member = response.data?.['hydra:member']
      return Array.isArray(member) ? member : []
    } catch {
      return []
    }
  }

  static async getFilterOptions(): Promise<ProjectFilterOptions> {
    const tryFilterOptionsEndpoint =
      !skipPlatformFilterOptionsEndpoint && !projectFilterOptionsEndpointSkippedForSession
    if (tryFilterOptionsEndpoint) {
      try {
        const response = await apiClient.get<ProjectFilterOptions>('/api/projects/filter_options', {
          validateStatus: (status) => status === 200 || status === 404 || status === 500,
        })
        if (response.status === 200 && response.data) {
          const normalized = ProjectService.normalizeProjectFilterOptions(response.data)
          if (ProjectService.isProjectFilterOptionsPopulated(normalized)) {
            return normalized
          }
        }
        if (response.status === 500 || response.status === 404) {
          projectFilterOptionsEndpointSkippedForSession = true
        }
      } catch {
        projectFilterOptionsEndpointSkippedForSession = true
      }
    }
    const projects = await ProjectService.getAll()
    return ProjectService.deriveProjectFilterOptionsFromProjects(projects)
  }

  private static isProjectFilterOptionsPopulated(o: ProjectFilterOptions): boolean {
    return (
      o.status.length > 0 ||
      o.conventionAvailable.length > 0 ||
      o.attributionInstance.length > 0 ||
      o.economicNature.length > 0 ||
      o.fundingYear.length > 0 ||
      o.fundingAmount.length > 0 ||
      o.sector.length > 0 ||
      o.fundingType.length > 0
    )
  }

  /**
   * Builds facet lists from the project collection when GET /api/projects/filter_options is unavailable.
   */
  static deriveProjectFilterOptionsFromProjects(projects: Project[]): ProjectFilterOptions {
    const status = new Set<string>()
    const attributionInstance = new Set<string>()
    const economicNature = new Set<string>()
    const sector = new Set<string>()
    const fundingType = new Set<string>()
    const conventionVals = new Set<boolean>()
    const years = new Set<number>()
    const amounts = new Set<number>()

    for (const p of projects) {
      ProjectService.addNonEmptyStringToSet(status, p.status)
      ProjectService.addNonEmptyStringToSet(
        attributionInstance,
        ProjectService.readProjectScalar(p, 'attributionInstance'),
      )
      ProjectService.addNonEmptyStringToSet(
        economicNature,
        ProjectService.readProjectScalar(p, 'economicNature'),
      )
      ProjectService.addNonEmptyStringToSet(sector, ProjectService.readProjectScalar(p, 'sector'))
      ProjectService.addNonEmptyStringToSet(
        fundingType,
        ProjectService.readProjectScalar(p, 'fundingType'),
      )

      const conv = ProjectService.readProjectScalar(p, 'conventionAvailable')
      if (conv === true || conv === 'true' || conv === 1 || conv === '1') conventionVals.add(true)
      else if (conv === false || conv === 'false' || conv === 0 || conv === '0') conventionVals.add(false)

      const yearRaw = ProjectService.readProjectScalar(p, 'fundingYear')
      const yearNum = ProjectService.coerceFiniteNumber(yearRaw)
      if (yearNum != null) years.add(Math.trunc(yearNum))

      const amountNum = ProjectService.coerceFiniteNumber(
        ProjectService.readProjectScalar(p, 'fundingAmount'),
      )
      if (amountNum != null) amounts.add(amountNum)
    }

    return {
      status: [...status].sort((a, b) => a.localeCompare(b)),
      conventionAvailable: [...conventionVals].sort((a, b) => Number(a) - Number(b)),
      attributionInstance: [...attributionInstance].sort((a, b) => a.localeCompare(b)),
      economicNature: [...economicNature].sort((a, b) => a.localeCompare(b)),
      fundingYear: [...years].sort((a, b) => a - b),
      fundingAmount: [...amounts].sort((a, b) => a - b),
      sector: [...sector].sort((a, b) => a.localeCompare(b)),
      fundingType: [...fundingType].sort((a, b) => a.localeCompare(b)),
    }
  }

  private static addNonEmptyStringToSet(target: Set<string>, raw: unknown): void {
    if (raw == null) return
    const s = String(raw).trim()
    if (s !== '') target.add(s)
  }

  private static coerceFiniteNumber(raw: unknown): number | null {
    if (raw == null || raw === '') return null
    const n = typeof raw === 'number' ? raw : Number(raw)
    if (typeof n !== 'number' || Number.isNaN(n) || !Number.isFinite(n)) return null
    return n
  }

  static normalizeProjectFilterOptions(raw: ProjectFilterOptions | null | undefined): ProjectFilterOptions {
    const empty = createEmptyProjectFilterOptions()
    if (raw == null || typeof raw !== 'object') return empty
    return {
      status: Array.isArray(raw.status) ? raw.status.map((s) => String(s)) : empty.status,
      conventionAvailable: Array.isArray(raw.conventionAvailable)
        ? raw.conventionAvailable.filter((x): x is boolean => typeof x === 'boolean')
        : empty.conventionAvailable,
      attributionInstance: Array.isArray(raw.attributionInstance)
        ? raw.attributionInstance.map((s) => String(s))
        : empty.attributionInstance,
      economicNature: Array.isArray(raw.economicNature)
        ? raw.economicNature.map((s) => String(s))
        : empty.economicNature,
      fundingYear: Array.isArray(raw.fundingYear)
        ? raw.fundingYear.map((n) => Number(n)).filter((n) => !Number.isNaN(n))
        : empty.fundingYear,
      fundingAmount: Array.isArray(raw.fundingAmount)
        ? raw.fundingAmount.map((n) => Number(n)).filter((n) => !Number.isNaN(n))
        : empty.fundingAmount,
      sector: Array.isArray(raw.sector) ? raw.sector.map((s) => String(s)) : empty.sector,
      fundingType: Array.isArray(raw.fundingType)
        ? raw.fundingType.map((s) => String(s))
        : empty.fundingType,
    }
  }

  /**
   * GET /api/projects with optional filters. Applies client-side filtering when the API ignores params.
   * @param options.cmnCode Same commune-perimeter scoping as getAll(); combined with the other filters.
   */
  static async getCollection(
    filters: ProjectMapFilters,
    options?: { itemsPerPage?: number; cmnCode?: string | null },
  ): Promise<Project[]> {
    const itemsPerPage = options?.itemsPerPage ?? PROJECT_COLLECTION_MAX_ITEMS_PER_PAGE
    const cmnCode = options?.cmnCode
    const query: Record<string, string> = { itemsPerPage: String(itemsPerPage) }
    if (cmnCode) {
      query.cmnCode = cmnCode
    }
    const stringEntries: [keyof ProjectMapFilters, string][] = [
      ['status', filters.status],
      ['attributionInstance', filters.attributionInstance],
      ['economicNature', filters.economicNature],
      ['sector', filters.sector],
      ['fundingType', filters.fundingType],
    ]
    for (const [key, value] of stringEntries) {
      const trimmed = value.trim()
      if (trimmed !== '') {
        query[key] = trimmed
      }
    }
    const convention = filters.conventionAvailable.trim()
    if (convention === 'true' || convention === 'false') {
      query.conventionAvailable = convention
    }
    let list: Project[] = []
    try {
      const member = await apiClient
        .get('/api/projects', { params: query })
        .then((response) => response.data?.['hydra:member'])
      list = Array.isArray(member) ? member : []
    } catch {
      // Keep the commune scope on fallback so a filter-param quirk never leaks unscoped/wrong-commune data.
      list = await ProjectService.getAll(cmnCode)
    }
    return ProjectService.applyClientSideProjectMapFilters(list, filters)
  }

  static applyClientSideProjectMapFilters(
    projects: Project[],
    filters: ProjectMapFilters,
  ): Project[] {
    const status = filters.status.trim()
    const convention = filters.conventionAvailable.trim()
    const attributionInstance = filters.attributionInstance.trim()
    const economicNature = filters.economicNature.trim()
    const sector = filters.sector.trim()
    const fundingType = filters.fundingType.trim()

    const yearMinParsed = ProjectService.parseOptionalNumberBound(filters.fundingYearMin)
    const yearMaxParsed = ProjectService.parseOptionalNumberBound(filters.fundingYearMax)
    const amountMinParsed = ProjectService.parseOptionalNumberBound(filters.fundingAmountMin)
    const amountMaxParsed = ProjectService.parseOptionalNumberBound(filters.fundingAmountMax)

    return projects.filter((project) => {
      if (status !== '' && String(project.status ?? '').trim() !== status) return false
      if (convention === 'true' || convention === 'false') {
        const raw = ProjectService.readProjectScalar(project, 'conventionAvailable')
        const boolVal = raw === true || raw === 'true' || raw === 1 || raw === '1'
        if (convention === 'true' && !boolVal) return false
        if (convention === 'false' && boolVal) return false
      }
      if (
        attributionInstance !== '' &&
        String(ProjectService.readProjectScalar(project, 'attributionInstance') ?? '').trim() !==
          attributionInstance
      )
        return false
      if (
        economicNature !== '' &&
        String(ProjectService.readProjectScalar(project, 'economicNature') ?? '').trim() !==
          economicNature
      )
        return false
      if (sector !== '' && String(ProjectService.readProjectScalar(project, 'sector') ?? '').trim() !== sector)
        return false
      if (
        fundingType !== '' &&
        String(ProjectService.readProjectScalar(project, 'fundingType') ?? '').trim() !== fundingType
      )
        return false
      if (yearMinParsed != null || yearMaxParsed != null) {
        const y = ProjectService.readProjectScalar(project, 'fundingYear')
        const yn = typeof y === 'number' ? y : y != null ? Number(y) : NaN
        if (Number.isNaN(yn)) return false
        if (yearMinParsed != null && yn < yearMinParsed) return false
        if (yearMaxParsed != null && yn > yearMaxParsed) return false
      }
      if (amountMinParsed != null || amountMaxParsed != null) {
        const a = ProjectService.readProjectScalar(project, 'fundingAmount')
        const an = typeof a === 'number' ? a : a != null ? Number(a) : NaN
        if (Number.isNaN(an)) return false
        if (amountMinParsed != null && an < amountMinParsed) return false
        if (amountMaxParsed != null && an > amountMaxParsed) return false
      }
      return true
    })
  }

  private static readProjectScalar(project: Project, key: string): unknown {
    return (project as unknown as Record<string, unknown>)[key]
  }

  private static parseOptionalNumberBound(raw: string): number | null {
    const trimmed = raw.trim()
    if (trimmed === '') return null
    const n = Number(trimmed)
    if (Number.isNaN(n) || !Number.isFinite(n)) return null
    return n
  }

  static async get(project: Partial<Project>): Promise<Project> {
    const { id } = project
    return await apiClient.get('/api/projects/' + id).then((response) => response.data)
  }

  static async getBySlug(slug: string): Promise<Project> {
    return await apiClient
      .get('/api/projects', { params: { slug } })
      .then((response) => response.data['hydra:member'][0])
  }

  static async post(project: ProjectSubmission): Promise<Project> {
    return await apiClient.post('/api/projects', project).then((response) => response.data)
  }

  static async patch(project: Partial<ProjectSubmission | Project>): Promise<Project> {
    return await apiClient
      .patch('/api/projects/' + project.id, project)
      .then((response) => response.data)
  }

  static async delete(project: Project): Promise<Project> {
    return await apiClient.delete('/api/projects/' + project.id).then((response) => response.data)
  }

  static async getSimilarProjects(project: Project): Promise<Project[]> {
    return await apiClient
      .get('/api/projects/' + project.id + '/similar')
      .then((response) => response.data['hydra:member'])
  }

  static async createOrUpdate(
    projectToSubmit: ProjectSubmission,
    type: FormType,
  ): Promise<Project> {
    let symfonyProject = transformSymfonyRelationToIRIs<Project>(projectToSubmit)

    const project =
      type === FormType.CREATE ? await this.post(projectToSubmit) : await this.patch(symfonyProject)

    if (type === FormType.CREATE) {
      symfonyProject.id = project.id
    }

    if (projectToSubmit.logoToUpload) {
      symfonyProject.logo = (await FileUploader.uploadMedia(
        projectToSubmit.logoToUpload.file,
      )) as BaseMediaObject
    }

    const images = await Promise.all(
      projectToSubmit.imagesToUpload.map(async (img) => await FileUploader.uploadMedia(img.file)),
    )

    if (images.length > 0) {
      symfonyProject.images.push(...(images as BaseMediaObject[]))
    } else if (projectToSubmit.images.length === 0) {
      symfonyProject.images = []
    }

    const partnerImages = await Promise.all(
      projectToSubmit.imagesPartnerToUpload.map(
        async (img) => await FileUploader.uploadMedia(img.file),
      ),
    )
    if (partnerImages.length > 0) {
      symfonyProject.partners.push(...(partnerImages as BaseMediaObject[]))
    } else if (projectToSubmit.partners.length === 0) {
      symfonyProject.partners = []
    }
    symfonyProject = transformSymfonyRelationToIRIs<Project>(symfonyProject)
    if (
      symfonyProject.id &&
      (images.length > 0 || projectToSubmit.logoToUpload || partnerImages.length > 0)
    ) {
      return await this.patchImages(symfonyProject)
    }

    return project
  }

  static async patchImages(project: Project): Promise<Project> {
    return this.patch({
      images: project.images,
      id: project.id,
      logo: project.logo,
      partners: project.partners,
    })
  }
}
